import React, { useMemo, useState, memo } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { StatCard } from '../../shared/StatCard';
import { SeedStatus } from '../system/diagnostics/SeedStatus';
import { LoadedModulesWidget } from '../system/diagnostics/LoadedModulesWidget';
import { OrphanStatus } from '../system/diagnostics/OrphanStatus';
import { CategoryCoverageWidget } from '../system/diagnostics/CategoryCoverageWidget';
import { computeLiveSystemStats } from '../../../governance';
import { QUESTIONS_BY_FAMILY, TAXONOMY } from '../../../content';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { AdminTabs } from '../shared/AdminTabs';
import { Database, GitBranch, Cpu, Clock, HardDrive, Activity, Hash, Tag } from 'lucide-react';
import { dataLayer } from '../../../data/dataLayer';
const tabs = [{
  id: 'seeds',
  label: 'Seeds',
  icon: <Database className="w-4 h-4" data-id="element-6956" data-mp="2gp5ak9w8cu3r" />
}, {
  id: 'system',
  label: 'Systeem',
  icon: <Cpu className="w-4 h-4" data-id="element-6957" data-mp="2cv7m8kq59c3j" />
}, {
  id: 'orphans',
  label: 'Orphans',
  icon: <Activity className="w-4 h-4" data-id="element-6958" data-mp="2hshdm5x3fxu0" />
}] as const;
type TabId = (typeof tabs)[number]['id'];
function ResourceBar({
  label,
  value,
  color
}: {
  label: string;
  value: number | string;
  color: string;
}) {
  const isNumber = typeof value === 'number';
  const displayValue = isNumber ? `${value}%` : value;
  const percentValue = isNumber ? value : 0;
  return <div data-id="element-6959" data-mp="1z3u8jnex38gf">
      <div className="flex justify-between text-xs mb-1.5" data-id="element-6960" data-mp="1z3kpkjsai1k8">
        <span className="text-gray-700 font-medium" data-id="element-6961" data-mp="1z0ybs2et2ptk">{label}</span>
        <span className={`font-semibold ${isNumber && value > 80 ? 'text-red-500' : isNumber && value > 60 ? 'text-yellow-600' : 'text-gray-600'}`} data-id="element-6962" data-mp="1k7lc0tmev97p">
          {displayValue}
        </span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-6963" data-mp="3ehynyy70u821">
        <div className={`${color} h-2 rounded-full transition-all`} style={{
        width: `${percentValue}%`
      }} data-id="element-6964" data-mp="3ji37yy751kx0" />
      </div>
    </div>;
}
function SystemPanel() {
  const {
    accounts
  } = useAppStore();
  const diagnostics = useMemo(() => {
    const storageStats = dataLayer.getStorageStats();
    const storageMb = storageStats.usedMb;
    const activeAccounts = accounts?.accountsById ? Object.values(accounts.accountsById).filter(a => Date.now() - (a.lastActive || 0) < 60 * 60 * 1000).length : 0;
    let responseTime = 45;
    try {
      if (window.performance && window.performance.timing) {
        responseTime = window.performance.timing.responseEnd - window.performance.timing.requestStart;
      }
    } catch {
      // Performance timing API may be unavailable
    }
    return {
      storageMb,
      activeAccounts,
      responseTime: Math.max(10, responseTime)
    };
  }, [accounts]);
  const resourceMetrics = useMemo(() => {
    let memoryUsage = 0;
    try {
      const perfWithMemory = window.performance as Performance & {
        memory?: {
          usedJSHeapSize: number;
          jsHeapSizeLimit: number;
        };
      };
      if (perfWithMemory.memory) {
        memoryUsage = Math.round(perfWithMemory.memory.usedJSHeapSize / perfWithMemory.memory.jsHeapSizeLimit * 100);
      } else {
        memoryUsage = Math.min(100, Math.round(parseFloat(diagnostics.storageMb) / 5 * 100));
      }
    } catch {
      // Memory API may be unavailable in some browsers
    }
    const storageUsage = Math.min(100, Math.round(parseFloat(diagnostics.storageMb) / 5 * 100));
    return {
      memory: memoryUsage,
      storage: storageUsage
    };
  }, [diagnostics.storageMb]);
  const environmentRows = [{
    label: 'Build mode',
    value: typeof import.meta !== 'undefined' && import.meta.env?.MODE ? import.meta.env.MODE : 'development'
  }, {
    label: 'Lokale opslag',
    value: `${diagnostics.storageMb} MB`
  }, {
    label: 'Actieve accounts',
    value: diagnostics.activeAccounts.toString()
  }];
  return <div className="space-y-6" data-id="element-6965" data-mp="3403c149k2yxq">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-6966" data-mp="3437k4cq56eto">
        <StatCard label="Laadtijd" value={`${diagnostics.responseTime}ms`} subtitle="gemiddeld" icon={<Clock className="w-5 h-5" data-id="element-6968" data-mp="2beafhceusgje" />} color="amber" data-id="element-6967" data-mp="2z3ctmaaa3r95" />
        <StatCard label="Opslag" value={`${diagnostics.storageMb} MB`} subtitle="Lokale opslag" icon={<HardDrive className="w-5 h-5" data-id="element-6970" data-mp="2l8aj19uhldq0" />} color="blue" data-id="element-6969" data-mp="26exivzy6jv3m" />
        <StatCard label="Actieve Sessies" value={diagnostics.activeAccounts} subtitle="Laatste uur" icon={<Activity className="w-5 h-5" data-id="element-6972" data-mp="3rqz84dl1ogas" />} color="purple" data-id="element-6971" data-mp="2ga8ysllbr8cy" />
      </div>

      <LoadedModulesWidget data-id="element-6973" data-mp="34u568hywbs3" />

      <CategoryCoverageWidget data-id="element-6974" data-mp="34koxcmq5izl" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6" data-id="element-6975" data-mp="339dqrqy98yg">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-6976" data-mp="84ynm42frgky">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-6977" data-mp="3qablfk4u0dur">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-6978" data-mp="3ualsl04d1ab7">
              Systeembronnen
            </h3>
          </div>
          <div className="p-4 sm:p-5 space-y-4" data-id="element-6979" data-mp="lwdawn3dm3xt">
            <ResourceBar label="Geheugen" value={resourceMetrics.memory} color="bg-purple-500" data-id="element-6980" data-mp="quf25vayh9kd" />
            <ResourceBar label="Opslag" value={resourceMetrics.storage} color="bg-green-500" data-id="element-6981" data-mp="d28l86zzayjv" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-6982" data-mp="14txx1meraoeq">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-6983" data-mp="19lfvo3tb4cwj">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-6984" data-mp="1jgmvrm16oxk2">Omgeving</h3>
          </div>
          <div className="divide-y divide-gray-50" data-id="element-6985" data-mp="1ffbd60gq9cld">
            {environmentRows.map((row, i) => <div key={i} className="px-4 sm:px-5 py-3 flex justify-between items-center text-sm" data-id="element-6986" data-mp="1ag8auns4tkh3" data-mpi={i}>
                <span className="text-gray-600" data-id="element-6987" data-mp="15gc3cek87aws">{row.label}</span>
                <span className="font-medium text-gray-900" data-id="element-6988" data-mp="10ikdrr7200vr">{row.value}</span>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
export function DiagnosticsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('seeds');
  const stats = useMemo(() => computeLiveSystemStats(), []);
  return <PageLayout title="Systeem Diagnostiek" subtitle="Technische status en systeembronnen" actions={<ConnectionBadge status="live" data-id="element-6990" data-mp="hqri1p0lo1kc" />} breadcrumbs={[{
    label: 'Systeem'
  }, {
    label: 'Diagnostiek'
  }]} data-id="element-6989" data-mp="mrxg82d14h19">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-6991" data-mp="2qm12nvgcvi4">
        <StatCard label="Totaal Vragen" value={stats.totalQuestions.toLocaleString('nl-NL')} icon={<Hash className="w-5 h-5" data-id="element-6993" data-mp="cnh15b7dj3yi" />} color="blue" data-id="element-6992" data-mp="7mb79a1z9ane" />
        <StatCard label="Families" value={QUESTIONS_BY_FAMILY.size} icon={<Tag className="w-5 h-5" data-id="element-6995" data-mp="3g4gzvlfdy29l" />} color="purple" data-id="element-6994" data-mp="hkh59gknxwmz" />
        <StatCard label="Taxonomie" value={TAXONOMY.length} icon={<GitBranch className="w-5 h-5" data-id="element-6997" data-mp="3q1lm1l98dxqe" />} color="emerald" data-id="element-6996" data-mp="3l1zbxkop8xlt" />
        <StatCard label="Geheugen" value={`${Math.round(stats.memoryUsageMB)} MB`} icon={<Cpu className="w-5 h-5" data-id="element-6999" data-mp="3uu4zmi7x4sv7" />} color="amber" data-id="element-6998" data-mp="3pzis4q4yjkge" />
      </div>

      <AdminTabs tabs={tabs} activeTab={activeTab} onTabChange={id => setActiveTab(id as TabId)} data-id="element-7000" data-mp="2xaty1b2bkfe5" />

      <div className="mt-4" data-id="element-7001" data-mp="kctossyydokw">
        {activeTab === 'seeds' && <SeedStatus data-id="element-7002" data-mp="kddbemzjt159" />}
        {activeTab === 'system' && <SystemPanel data-id="element-7003" data-mp="gc01o1tc4dt2" />}
        {activeTab === 'orphans' && <OrphanStatus data-id="element-7004" data-mp="gdb5g1ms5lk6" />}
      </div>
    </PageLayout>;
}